class StockistLocator extends HTMLElement {
  constructor() {
    super();
    this.markers = new Map();
    this.activeFilter = 'all';
    this.userLocation = null;
  }

  connectedCallback() {
    const sectionId = this.dataset.sectionId;
    const dataScript = this.querySelector('script[data-stockist-data]');

    if (!dataScript) return;

    try {
      this.stockists = JSON.parse(dataScript.textContent);
    } catch (err) {
      console.error('Stockist locator: could not parse stockist data', err);
      this.stockists = [];
    }

    this.mapEl = this.querySelector(`#stockist-map-${sectionId}`);
    this.listEl = this.querySelector(`#stockist-list-${sectionId}`);
    this.searchInput = this.querySelector('.stockist-locator__search');
    this.locateBtn = this.querySelector('.stockist-locator__locate-btn');
    this.chips = this.querySelectorAll('.stockist-locator__chip');
    this.rows = this.querySelectorAll('.stockist-locator__row');

    this.initMap();
    this.bindRowEvents();
    this.bindSearch();
    this.bindFilters();
    this.bindLocate();
  }

  initMap() {
    if (!window.L || !this.mapEl || this.stockists.length === 0) return;

    this.map = L.map(this.mapEl, { zoomControl: true, scrollWheelZoom: false })
      .setView([this.stockists[0].lat, this.stockists[0].lng], 10);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_2vvw_1_c3e6974fb90e74387d80a517', {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      subdomains: 'abcd',
      maxZoom: 19
    }).addTo(this.map);

    const roseIcon = L.divIcon({
      className: 'stockist-locator__marker',
      html: '<svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg"><path d="M13 0C5.8 0 0 5.8 0 13c0 9.75 13 21 13 21s13-11.25 13-21C26 5.8 20.2 0 13 0z" fill="#d98a8a"/><circle cx="13" cy="13" r="5.5" fill="#121110"/></svg>',
      iconSize: [26, 34],
      iconAnchor: [13, 34],
      popupAnchor: [0, -30]
    });

    this.rows.forEach((row) => {
      const lat = parseFloat(row.dataset.lat);
      const lng = parseFloat(row.dataset.lng);
      const name = row.querySelector('.stockist-locator__name')?.textContent ?? '';
      const addr = row.querySelector('.stockist-locator__addr')?.textContent.trim() ?? '';

      const marker = L.marker([lat, lng], { icon: roseIcon }).addTo(this.map);
      marker.bindPopup(
        `<div class="stockist-locator__popup-name">${this.escapeHtml(name)}</div><div class="stockist-locator__popup-addr">${this.escapeHtml(addr)}</div>`
      );
      this.markers.set(row, marker);
    });
  }

  bindRowEvents() {
    this.rows.forEach((row) => {
      const activate = () => this.selectRow(row);
      row.addEventListener('click', activate);
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      });
    });
  }

  selectRow(row) {
    this.rows.forEach((r) => r.classList.remove('is-active'));
    row.classList.add('is-active');

    const marker = this.markers.get(row);
    if (marker && this.map) {
      this.map.setView(marker.getLatLng(), 13, { animate: true });
      marker.openPopup();
    }
  }

  bindSearch() {
    if (!this.searchInput) return;
    this.searchInput.addEventListener('input', () => {
      const query = this.searchInput.value.trim().toLowerCase();
      this.applyFilters(query);
    });
  }

  bindFilters() {
    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        this.chips.forEach((c) => c.classList.remove('is-active'));
        chip.classList.add('is-active');
        this.activeFilter = chip.dataset.filter;
        this.applyFilters(this.searchInput ? this.searchInput.value.trim().toLowerCase() : '');
      });
    });
  }

  applyFilters(query) {
    this.rows.forEach((row) => {
      let visible = true;

      if (this.activeFilter && this.activeFilter !== 'all') {
        const [key, value] = this.activeFilter.split(':');
        const rowValue = key === 'state' ? row.dataset.state : row.dataset.type;
        if (rowValue !== value) visible = false;
      }

      if (visible && query) {
        const haystack = row.textContent.toLowerCase();
        if (!haystack.includes(query)) visible = false;
      }

      row.classList.toggle('is-hidden', !visible);
    });
  }

  bindLocate() {
    if (!this.locateBtn) return;
    this.locateBtn.addEventListener('click', () => {
      if (!navigator.geolocation) return;

      this.locateBtn.textContent = 'Locating…';
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.sortByDistance();
          this.locateBtn.textContent = 'Near me';
        },
        () => {
          this.locateBtn.textContent = 'Near me';
        },
        { timeout: 8000 }
      );
    });
  }

  sortByDistance() {
    if (!this.userLocation || !this.listEl) return;

    const rowsArray = Array.from(this.rows);
    rowsArray.forEach((row) => {
      const lat = parseFloat(row.dataset.lat);
      const lng = parseFloat(row.dataset.lng);
      const dist = this.haversineDistance(this.userLocation.lat, this.userLocation.lng, lat, lng);
      row._distance = dist;

      const distEl = row.querySelector('[data-dist]');
      if (distEl) {
        distEl.textContent = `${dist.toFixed(1)} km away`;
        distEl.hidden = false;
      }
    });

    rowsArray.sort((a, b) => a._distance - b._distance);
    rowsArray.forEach((row) => this.listEl.appendChild(row));

    if (rowsArray.length > 0) {
      this.selectRow(rowsArray[0]);
    }
  }

  haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRad(deg) {
    return (deg * Math.PI) / 180;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('stockist-locator', StockistLocator);